import type { HvacSystemValues } from '../components/HvacSystemFields';
import type { PlumbingSystemValues } from '../components/PlumbingSystemFields';
import type { InspectionFormValues } from './inspectionReportFormDefaults';

/** Convert camelCase key to snake_case */
export function toSnakeCase(str: string): string {
  return str.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
}

export function systemToSnake(sys: object): Record<string, unknown> {
  return Object.fromEntries(Object.entries(sys).map(([key, value]) => [toSnakeCase(key), value]));
}

export interface BuildInspectionReportFormDataInput {
  homeInspectionJobId: string;
  formValues: InspectionFormValues;
  hvacSystems: HvacSystemValues[];
  plumbingSystems: PlumbingSystemValues[];
  hvacSystemPhotos: File[][];
  plumbingSystemPhotos: File[][];
  electricalPhotos: File[];
  waterTreatmentPhotos: File[];
  airQualityPhotos: File[];
}

export function buildInspectionReportFormData(input: BuildInspectionReportFormDataInput): FormData {
  const {
    homeInspectionJobId,
    formValues,
    hvacSystems,
    plumbingSystems,
    hvacSystemPhotos,
    plumbingSystemPhotos,
    electricalPhotos,
    waterTreatmentPhotos,
    airQualityPhotos,
  } = input;

  const formData = new FormData();
  formData.append('inspection_report[home_inspection_job_id]', homeInspectionJobId);
  formData.append('inspection_report[offered_maintenance_plan]', formValues.offeredMaintenancePlan);

  const hvacPayload = hvacSystems.map((sys) => systemToSnake(sys));
  formData.append('inspection_report[hvac_systems]', JSON.stringify(hvacPayload));

  const plumbingPayload = plumbingSystems.map((sys) => systemToSnake(sys));
  formData.append('inspection_report[plumbing_systems]', JSON.stringify(plumbingPayload));

  Object.entries(formValues).forEach(([key, value]) => {
    if (key.startsWith('electrical') || key.startsWith('waterTreatment') || key.startsWith('airQuality')) {
      formData.append(`inspection_report[${toSnakeCase(key)}]`, String(value));
    }
  });

  hvacSystemPhotos.forEach((files, sysIdx) => {
    files.forEach((file) => {
      formData.append(`inspection_report[hvac_systems_photos][${sysIdx}][]`, file);
    });
  });

  plumbingSystemPhotos.forEach((files, sysIdx) => {
    files.forEach((file) => {
      formData.append(`inspection_report[plumbing_systems_photos][${sysIdx}][]`, file);
    });
  });

  electricalPhotos.forEach((file) => formData.append('inspection_report[electrical_photos][]', file));
  waterTreatmentPhotos.forEach((file) => formData.append('inspection_report[water_treatment_photos][]', file));
  airQualityPhotos.forEach((file) => formData.append('inspection_report[air_quality_photos][]', file));

  return formData;
}
